import React, { useRef } from "react";
import cns from "classnames";
import { useObserver } from "mobx-react";
import { Popover, withStyles } from "@material-ui/core";
import { isEmpty } from "lodash";

import { Editor } from "@tinymce/tinymce-react";
import { store as rootStore } from "@store";
import locale from "@bonliva-crm/locale";
import { Classes, ThemeType } from "@theme";

import { AddRecipientChip, AddRecipientForm, AvatarChip, Body } from "@components";
import { Label as FormLabel } from "@components/typography/FormLabel";
import { Form, FormxDebugger, Text } from "@components/formx";
import { DnDLocalContainer } from "@containers/DnDContainer";
import { EmailEditorFormStore } from "./EmailEditorFormStore";
import { EmailEditorAttachmentList } from "./EmailEditorAttachmentList";
import { Select } from "@common/formx_item_form";

type Props = {
  classes: Classes<typeof styles>;
  store: EmailEditorFormStore;
  hideFrom?: boolean;
  smallChips?: boolean;
  inlineRecipients?: boolean;
  withToEditor?: boolean;
  withAddRecipient?: boolean;
  withRemoveRecipient?: boolean; // adds Delete icon to chips
  customAttachment?: React.ReactNode;
  fromOptions?: string[];

  renderSuggestions?: (options: any) => React.ReactNode;
};

function EmailEditorForm(props: Props) {
  const addRecipientRef = useRef(null);

  const { classes, store, withToEditor } = props;
  const { myProfile } = rootStore().user;

  const chipSize = props.smallChips
    ? "small"
    : store.values.to.length > 10
      ? "small"
      : "normal";

  return useObserver(() => {
    return (
      <Form store={store} allowUpdates>
        {!props.hideFrom && (
          <div className={classes.fieldRow}>
            <Body className={classes.fieldTitle}>{locale.From}:</Body>
            {store.values.from ? (
              <AvatarChip
                size={chipSize}
                name={null}
                src={null}
                ChipProps={{
                  label: <span>{store.values.from}</span>
                }}
              />
            ) : (
              <AvatarChip size="normal" name={myProfile.Name} src={myProfile.AvatarPic} />
            )}
            {props.fromOptions?.length > 0 && (
              <div style={{ maxWidth: "300px", width: "100%", marginLeft: "auto" }}>
                <Select
                  label={locale.SendAs}
                  name="from"
                  options={props.fromOptions.toLabelValue()}
                />
              </div>
            )}
          </div>
        )}

        <DnDLocalContainer
          noClick
          onDrop={store.attachFiles}
          overlayProps={{ style: { margin: -24 } }}
          dropzoneOptions={{
            maxSize: 4_000_000
          }}
        >
          <div
            className={cns(classes.fieldRow, {
              [classes.fieldUnderline]: !props.withToEditor,
              [classes.inlineRecipients]: props.inlineRecipients
            })}
          >
            {withToEditor && (
              <Text
                name="to"
                TextFieldProps={{
                  label: locale.FillSomething(locale.To),
                  onChange: (e) => store.setValue("to[0].email", e.target.value),
                  value:
                    store.values.to && store.values.to.length
                      ? store.values.to[0].email
                      : ""
                }}
              />
            )}

            {!withToEditor && (
              <>
                <Body className={classes.fieldTitle}>{locale.To}:</Body>
                <div className={cns(classes.sendTo, chipSize)}>
                  {store.values.to.map((recipient) => {
                    return (
                      <AvatarChip
                        key={recipient.email}
                        size={chipSize}
                        name={recipient.name}
                        src={recipient.avatarPic}
                        ChipProps={{
                          onDelete: props.withRemoveRecipient
                            ? () => store.removeRecipient(recipient)
                            : undefined,
                          label: recipient.name ? (
                            <>
                              <span>
                                <strong>{recipient.name + " "}</strong>
                              </span>
                              <span>{recipient.email}</span>
                            </>
                          ) : (
                            <span>{recipient.email}</span>
                          )
                        }}
                      />
                    );
                  })}

                  {props.withAddRecipient && (
                    <AddRecipientChip
                      onClick={() => (store.showAddRecipient = true)}
                      small={chipSize === "small"}
                      ref={addRecipientRef}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          <div className={classes.fieldRow}>
            <Text label={locale.Subject} name="subject" />
          </div>

          <div style={{ position: "relative" }}>
            <Editor
              onEditorChange={(content) => store.setValue("body", content)}
              value={store.values.body}
              plugins="autoresize"
              init={{
                // content_css: "/tinymce.css",
                style: "border-bottom-style: 'dashed'",
                menubar: false,
                statusbar: false,
                plugins: "link image code paste emoticons",
                paste_as_text: true,
                min_height: 175,
                max_height: 600,
                toolbar:
                  "undo redo | bold italic | alignleft aligncenter alignright | code",
                // fontsize_formats: "8pt 10.5pt 11pt 12pt 14pt 18pt 24pt 36pt",
                setup: (ed) => {
                  ed.on("init", () => {
                    ed.execCommand("fontName", false, "Arial");
                    ed.execCommand("fontSize", false, "10.5pt");
                  });
                }
              }}
            />

            <div className={classes.editorLabel}>
              <FormLabel>{locale["Press Shift+Enter for a new line"]}</FormLabel>
            </div>
          </div>

          {props.customAttachment ? props.customAttachment : null}
          <EmailEditorAttachmentList store={store} />
        </DnDLocalContainer>

        <Popover
          open={store.showAddRecipient}
          disablePortal
          anchorEl={addRecipientRef.current}
          anchorOrigin={{ horizontal: "right", vertical: "top" }}
          transformOrigin={{ horizontal: "left", vertical: "top" }}
          onClose={() => (store.showAddRecipient = false)}
          PaperProps={{
            style: { width: 520 }
          }}
          transitionDuration={0}
        >
          <AddRecipientForm
            onSubmit={(values) => {
              store.addRecipient(values.name, values.email, values.avatarPic);
              store.showAddRecipient = false;
            }}
            variant="email"
            renderSuggestions={props.renderSuggestions}
            suggestions={
              !isEmpty(store.initialValues?.suggestions)
                ? store.initialValues.suggestions
                : store.initialValues.to.map((x) => ({ label: x.email, value: x.email }))
            }
          />
        </Popover>
        <FormxDebugger store={store} />
      </Form>
    );
  });
}

const styles = (theme: ThemeType) => ({
  "@global": {
    ".tox.tox-tinymce": {
      borderBottomStyle: "dashed"
    }
  },

  fieldRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: 8
  },

  fieldUnderline: {
    borderBottom: "1px solid #5b5b5bb8"
  },

  inlineRecipients: {
    display: "flex",
    alignItems: "center"
  },

  fieldTitle: {
    fontSize: 14 * 0.75,
    marginBottom: 5,
    opacity: 0.6
  },

  sendTo: {
    overflow: "auto",
    maxHeight: 120,

    "&.small > *": {
      margin: "0 3px 3px 0"
    },

    "&.normal > *": {
      margin: "0 5px 5px 0"
    }
  },

  editorLabel: {
    position: "absolute",
    bottom: 0,
    right: 0,
    padding: 4,
    color: theme.palette.grey[400]
  }
});

export default withStyles(styles as any)(EmailEditorForm);
